import { ChangeEvent, SyntheticEvent, useEffect, useState } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { useMutation, useQueryClient } from "react-query";
import { defaultInvalidateQueryFilters, queryKeys } from "../../../api/query";
import { Panel } from "primereact/panel";
import { useNavigate, useParams } from "react-router-dom";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { useCampusActivities } from "../../../api/queries/enum/useCampusActivities";
import {
  CreateCampusActivityPayload,
  UpdateCampusActivityPayload,
} from "../../../api/types/enum";
import { createCampusActivityRequest } from "../../../api/queries/enum/createCampusActivity";
import { updateCampusActivityRequest } from "../../../api/queries/enum/updateCampusActivity";
import { deleteCampusActivityRequest } from "../../../api/queries/enum/deleteCampusActivity";

export default function CampusActivityForm() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uuid } = useParams();

  const createCampusActivity = useMutation({
    mutationFn: (payload: CreateCampusActivityPayload) =>
      createCampusActivityRequest(payload),
  });

  const updateCampusActivity = useMutation({
    mutationFn: (payload: UpdateCampusActivityPayload) =>
      updateCampusActivityRequest(uuid, payload),
  });

  const deleteCampusActivity = useMutation({
    mutationFn: () => deleteCampusActivityRequest(uuid),
  });

  const { data: campusActivityResponse } = useCampusActivities();

  const [name, setName] = useState<string>("");
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void =>
    setName(event.target.value);

  const [parent, setParent] = useState<string>("");
  const onParentChange = (event: DropdownChangeEvent) => {
    setParent(event.value);
  };

  const handleSubmit = async (event: SyntheticEvent) => {
    event.preventDefault();

    if (uuid) {
      const payload: UpdateCampusActivityPayload = {
        name,
        parent_id: parent || null,
      };

      await updateCampusActivity.mutateAsync(payload);
    } else {
      const payload: CreateCampusActivityPayload = {
        name,
        parent_id: parent || null,
      };

      await createCampusActivity.mutateAsync(payload);
    }

    queryClient.invalidateQueries(
      queryKeys.campusActivities(),
      defaultInvalidateQueryFilters,
    );

    navigate("/admin/campus-activities");
  };

  const onDelete = async () => {
    await deleteCampusActivity.mutateAsync();

    queryClient.invalidateQueries(
      queryKeys.campusActivities(),
      defaultInvalidateQueryFilters,
    );

    navigate("/admin/campus-activities");
  };

  useEffect(() => {
    if (!uuid || !campusActivityResponse) return;

    const campusActivity = campusActivityResponse.data.results.find(
      (item) => item.id === uuid,
    );

    if (campusActivity) {
      setName(campusActivity.name);
      setParent(campusActivity.parent?.id || "");
    }
  }, [uuid, campusActivityResponse]);

  return (
    <form onSubmit={handleSubmit} className="col-12">
      <Panel header="Campus Activity" toggleable={true} className="mb-3">
        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <InputText value={name} onChange={onNameChange} />
              <label>Name *</label>
            </div>
          </div>
        </div>

        <div className="p-fluid">
          <div className="field pt-3">
            <div className="p-float-label">
              <Dropdown
                value={parent}
                onChange={onParentChange}
                options={campusActivityResponse?.data.results || []}
                optionLabel="name"
                optionValue="id"
                showClear
              />
              <label>Parent</label>
            </div>
          </div>
        </div>
      </Panel>

      <div className="flex gap-1 justify-content-end">
        {uuid && (
          <Button
            type="button"
            label="Delete"
            className="p-button-danger p-button-sm"
            onClick={onDelete}
          />
        )}

        <Button
          type="submit"
          label="Save"
          className="p-button-main p-button-sm"
          disabled={!name}
        />
      </div>
    </form>
  );
}
